import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Compass, Calendar, Facebook, Instagram } from "lucide-react";
import { SOCIAL } from "../lib/social";
import { api } from "../lib/api";

/**
 * /share/reel-card — vertical 9:16 launch card for Instagram / TikTok / FB
 * Reels & Stories.
 *
 * Two ways to use it:
 *  • SCREENSHOT it on your phone → use as a Story / Reel cover still
 *  • SCREEN-RECORD this page for 5-10s → use the recording as the Reel itself
 *
 * Design intent: massive QR + massive URL + crystal-clear launch date +
 * "create your account NOW" CTA + FB/IG handles, all readable from the back
 * of a Reel feed at thumbnail size. No nav, no scroll, no clutter.
 *
 * Live data: campsite count auto-fetches from /api/campsites/stats so the
 * card never goes stale.
 */
const PUBLIC_URL =
  (process.env.REACT_APP_PUBLIC_URL || "").trim().replace(/\/$/, "") ||
  "https://camp-compass.uk";

const LAUNCH_DATE = new Date("2026-05-31T00:00:00Z");
const LAUNCH_LABEL = LAUNCH_DATE.toLocaleDateString("en-GB", {
  day: "numeric", month: "long", year: "numeric",
});

export default function ReelCard() {
  const [count, setCount] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/campsites/stats").then(({ data }) => {
      if (alive && typeof data?.total === "number") setCount(data.total);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const countLabel = count != null ? count.toLocaleString() : "11,000+";

  return (
    <div className="min-h-screen bg-[#fafafa] flex flex-col items-center py-6 px-4">
      {/* Helper bar (hidden in print/screenshot framing) */}
      <div className="w-full max-w-md mb-5 print:hidden">
        <div className="eyebrow mb-1.5">Reel Card · 9:16</div>
        <h1 className="font-display text-2xl mb-2">Launch reel card</h1>
        <p className="text-xs text-[#555] leading-relaxed">
          Screen-record this page on your phone for 5–10 seconds and post it
          as a Reel, or screenshot it for a Story cover. Vertical 9:16 ratio
          fits Instagram, TikTok and Facebook Reels natively. The QR opens
          the app — no app-store needed.
        </p>
      </div>

      {/* The card itself — fixed 9:16 ratio, 360×640 base (scales up on big screens) */}
      <div
        data-testid="reel-card"
        className="relative w-full max-w-[420px] aspect-[9/16] bg-gradient-to-b from-[#ff4500] via-[#ff6634] to-[#cc3300] text-white overflow-hidden shadow-2xl"
        style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
      >
        {/* Subtle radial noise overlay for texture */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 25% 15%, #fff 0px, transparent 1.5px), radial-gradient(circle at 75% 85%, #fff 0px, transparent 1.5px)",
            backgroundSize: "12px 12px, 14px 14px",
          }}
        />

        {/* Header brand strip */}
        <div className="relative z-10 flex items-center justify-between px-5 pt-5">
          <div className="flex items-center gap-2">
            <Compass className="w-5 h-5" strokeWidth={2.2} />
            <span className="font-display text-[15px] tracking-tight">CampCompass</span>
          </div>
          <span className="mono text-[10px] uppercase tracking-widest opacity-90">UK Edition</span>
        </div>

        {/* Main headline */}
        <div className="relative z-10 px-5 mt-6">
          <div className="inline-block bg-white text-[#ff4500] mono text-[10px] tracking-[0.2em] uppercase font-bold px-2 py-1 mb-3">
            Built by campers
          </div>
          <h2 className="font-display text-[36px] leading-[1.05] tracking-tight mb-2">
            The honest UK<br/>camping app.
          </h2>
          <p className="text-[13px] leading-snug text-white/95 max-w-[280px]">
            <strong className="text-white">{countLabel}</strong> sites · real prices · no ads · no tracking · a kind community.
          </p>
        </div>

        {/* Big QR + URL block — the conversion driver */}
        <div className="relative z-10 mx-5 mt-6 bg-white text-[#111] p-3 flex flex-col items-center" style={{ boxShadow: "0 6px 0 0 #111" }}>
          <QRCodeSVG
            value={PUBLIC_URL}
            size={180}
            level="M"
            bgColor="#ffffff"
            fgColor="#111111"
            aria-label="Scan to open CampCompass UK"
          />
          <div className="mt-2 mono text-[14px] font-bold tracking-tight text-center">
            camp-compass.uk
          </div>
          <div className="mt-0.5 text-[10px] text-[#666] text-center">
            Scan with your phone camera
          </div>
        </div>

        {/* Launch date pill */}
        <div className="relative z-10 mx-5 mt-4 bg-[#111] text-white px-4 py-3">
          <div className="flex items-center gap-2.5">
            <Calendar className="w-4 h-4 flex-shrink-0 text-[#ff4500]" strokeWidth={2.4} />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/70 mono">Going live</div>
              <div className="font-display text-[18px] leading-tight">{LAUNCH_LABEL}</div>
            </div>
          </div>
          <div className="mt-2 text-[11px] leading-snug text-white/85 border-t border-white/15 pt-2">
            <strong className="text-[#ff4500]">Install + sign up now</strong> — be first in when we launch. Founding-member perks for early campers.
          </div>
        </div>

        {/* Footer socials */}
        <div className="relative z-10 absolute bottom-0 left-0 right-0 px-5 pb-4 pt-3">
          <div className="flex items-center justify-center gap-4 text-[12px] mono">
            <span className="inline-flex items-center gap-1.5">
              <Facebook className="w-3.5 h-3.5" strokeWidth={2.2} />
              {SOCIAL.facebook.handle}
            </span>
            <span className="text-white/40">·</span>
            <span className="inline-flex items-center gap-1.5">
              <Instagram className="w-3.5 h-3.5" strokeWidth={2.2} />
              {SOCIAL.instagram.handle}
            </span>
          </div>
        </div>
      </div>

      {/* Usage hint (hidden in screenshots) */}
      <div className="w-full max-w-md mt-5 text-center print:hidden">
        <p className="text-[11px] text-[#888] leading-relaxed">
          <strong className="text-[#111]">📱 On phone:</strong> long-press the card → "Take screenshot" → post to Stories / Reels.<br/>
          <strong className="text-[#111]">🎬 For a Reel:</strong> open this URL on phone → screen-record 5–10 seconds with light camera shake → add a song in Reels editor.
        </p>
      </div>
    </div>
  );
}
